// PATH: src/components/modules/devtools/planner/panels/PlannerPanel.js

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

// NEW
import PlannerFileTriageDialog from "../PlannerFileTriageDialog";

/**
 * FE-001 — Planner Draft Viewer (read-only)
 * GUARD-001 — Human Approval Gates (UI + logical only)
 */

const PlannerPanel = () => {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");

  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState("");

  const [selectedRun, setSelectedRun] = useState(null);

  const [goals, setGoals] = useState([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState("");

  const [statusDraft, setStatusDraft] = useState("DRAFT");
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusError, setStatusError] = useState("");

  // NEW: planner output
  const [agentRunning, setAgentRunning] = useState(false);
  const [agentError, setAgentError] = useState("");
  const [plannerOutput, setPlannerOutput] = useState("");

  // NEW: LLM plan trigger
  const [llmRunning, setLlmRunning] = useState(false);
  const [llmError, setLlmError] = useState("");

  // NEW: Patch instruction builder
  const [patchRunning, setPatchRunning] = useState(false);
  const [patchError, setPatchError] = useState("");
  const [patchOutput, setPatchOutput] = useState("");

  // NEW: Patch preview + apply
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRunning, setPreviewRunning] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewResult, setPreviewResult] = useState(null);

  const [applyRunning, setApplyRunning] = useState(false);
  const [applyError, setApplyError] = useState("");
  const [applyResult, setApplyResult] = useState(null);

  // NEW: File triage (human selection for LLM context)
  const [triageOpen, setTriageOpen] = useState(false);

  const [triageApprovedPaths, setTriageApprovedPaths] = useState([]);

  // NEW: Frontend debug (shows request/response for troubleshooting)
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugReq, setDebugReq] = useState(null);
  const [debugRes, setDebugRes] = useState(null);
  const [debugErr, setDebugErr] = useState(null);

  const selectedPlannerSessionId = useMemo(() => {
    if (!selectedRun) return null;
    return (
      selectedRun.planner_session_id ??
      selectedRun.plannerSessionId ??
      selectedRun.session_id ??
      null
    );
  }, [selectedRun]);

  const fetchJson = async (url, opts, debugName) => {
    const method = (opts && opts.method) || "GET";
    const headers = (opts && opts.headers) || undefined;
    const bodyRaw = (opts && opts.body) || undefined;

    // Try to keep request body readable in debug panel
    let bodyPreview = bodyRaw;
    if (typeof bodyRaw === "string") {
      try {
        bodyPreview = JSON.parse(bodyRaw);
      } catch (e) {
        bodyPreview = bodyRaw;
      }
    }

    const t0 = Date.now();
    setDebugReq({
      name: debugName || "",
      url,
      method,
      headers,
      body: bodyPreview,
      started_at: new Date().toISOString(),
    });
    setDebugRes(null);
    setDebugErr(null);

    const res = await fetch(url, opts);

    const contentType = res.headers.get("content-type") || "";
    const isJson = contentType.includes("application/json");

    // Read once (avoid double-consuming the stream)
    const rawText = await res.text();
    let data = null;
    if (isJson) {
      try {
        data = JSON.parse(rawText || "{}");
      } catch (e) {
        data = { _parse_error: true, _raw: rawText };
      }
    }

    setDebugRes({
      name: debugName || "",
      status: res.status,
      ok: res.ok,
      ms: Date.now() - t0,
      body: isJson ? data : rawText,
      received_at: new Date().toISOString(),
    });

    if (!res.ok) {
      const body = isJson ? JSON.stringify(data) : rawText;
      const err = new Error(`HTTP ${res.status}: ${body}`);
      setDebugErr(err);
      throw err;
    }

    if (!isJson) {
      const err = new Error(
        `Expected JSON but got: ${(rawText || "").slice(0, 120)}...`
      );
      setDebugErr(err);
      throw err;
    }

    return data;
  };

  const loadProjects = async () => {
    setProjectsLoading(true);
    setProjectsError("");
    try {
      const data = await fetchJson(
        "/api/dev-ide/projects",
        undefined,
        "loadProjects"
      );

      // Expected shape: {success:true, projects:[{project_id,label,...}]}
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.projects)
        ? data.projects
        : [];

      setProjects(arr);

      // Default selection: keep existing, else first project_id.
      if (!selectedProjectId && arr.length > 0) {
        const firstId = arr[0]?.project_id || arr[0]?.projectId || "";
        if (firstId) setSelectedProjectId(String(firstId));
      }
    } catch (e) {
      setProjectsError(e?.message || "Failed to load projects.");
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  const loadRuns = async () => {
    setRunsLoading(true);
    setRunsError("");
    try {
      const data = await fetchJson(
        "/api/dev-ide/planner/runs",
        undefined,
        "loadRuns"
      );

      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.runs)
        ? data.runs
        : [];
      setRuns(arr);
    } catch (e) {
      setRunsError(e?.message || "Failed to load planner runs.");
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  };

  const loadGoals = async () => {
    setGoals([]);
    setGoalsError("");

    if (!selectedRun) return;

    setGoalsLoading(true);
    try {
      if (
        selectedPlannerSessionId === null ||
        selectedPlannerSessionId === undefined
      ) {
        throw new Error("Selected run has no planner_session_id.");
      }

      const url = `/api/dev-ide/planner/goals?planner_session_id=${encodeURIComponent(
        String(selectedPlannerSessionId)
      )}`;

      const data = await fetchJson(url, undefined, "loadGoals");

      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.goals)
        ? data.goals
        : [];
      setGoals(arr);
    } catch (e) {
      setGoalsError(e?.message || "Failed to load planner goals.");
      setGoals([]);
    } finally {
      setGoalsLoading(false);
    }
  };

  const syncStatusDraftFromSelected = (run) => {
    const s = run && run.status ? String(run.status).toUpperCase() : "DRAFT";
    if (s === "DRAFT" || s === "REVIEWED" || s === "APPROVED") {
      setStatusDraft(s);
    } else {
      setStatusDraft("DRAFT");
    }
  };

  const saveStatus = async () => {
    if (!selectedRun || !selectedRun.id) return;

    setStatusSaving(true);
    setStatusError("");
    try {
      const data = await fetchJson(
        `/api/dev-ide/planner/runs/${selectedRun.id}/set-status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: statusDraft }),
        },
        "setStatus"
      );

      if (!data?.success) {
        throw new Error(data?.error || "Failed to update status.");
      }

      const updated = data.run || null;
      if (updated) {
        setSelectedRun(updated);
      }

      await loadRuns();
    } catch (e) {
      setStatusError(e?.message || "Failed to update status.");
    } finally {
      setStatusSaving(false);
    }
  };

  const runAgent = async () => {
    if (!selectedRun || !selectedRun.id) return;

    // GUARD-001
    const s = (statusDraft || "DRAFT").toUpperCase();
    if (s === "APPROVED") {
      setAgentError(
        "Run is APPROVED. Set status back to DRAFT/REVIEWED before regenerating output."
      );
      return;
    }

    setAgentRunning(true);
    setAgentError("");
    setLlmError("");
    setPatchError("");
    setPlannerOutput("");
    setPatchOutput("");
    try {
      const data = await fetchJson(
        `/api/dev-ide/planner/runs/${selectedRun.id}/run-agent`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ top_n: 50, project_id: selectedProjectId }),
        },
        "runAgent"
      );

      if (!data?.success) {
        throw new Error(data?.error || "Failed to run agent.");
      }

      const block = data.instruction_block || null;
      const text = block?.instruction || "";
      setPlannerOutput(text);

      await loadRuns();
    } catch (e) {
      setAgentError(e?.message || "Failed to run agent.");
      setPlannerOutput("");
    } finally {
      setAgentRunning(false);
    }
  };

  // NEW: open triage dialog instead of calling run-llm directly
  const runLLM = async () => {
    if (!selectedRun || !selectedRun.id) return;

    // GUARD-001
    const s = (statusDraft || "DRAFT").toUpperCase();
    if (s === "APPROVED") {
      setLlmError(
        "Run is APPROVED. Set status back to DRAFT/REVIEWED before regenerating output."
      );
      return;
    }

    setLlmError("");
    setTriageOpen(true);
  };

  // NEW: actual LLM call after human selects files
  const runLLMWithFiles = async (files) => {
    if (!selectedRun || !selectedRun.id) return;

    // GUARD-001
    const s = (statusDraft || "DRAFT").toUpperCase();
    if (s === "APPROVED") {
      setLlmError(
        "Run is APPROVED. Set status back to DRAFT/REVIEWED before regenerating output."
      );
      return;
    }

    // NEW: require a project so backend can resolve roots from devide_projects
    if (!selectedProjectId) {
      setLlmError("Please select a project first.");
      return;
    }

    const chosen = Array.isArray(files) ? files.filter(Boolean) : [];

    setTriageApprovedPaths(chosen);
    setLlmRunning(true);
    setLlmError("");
    setAgentError("");
    setPatchError("");
    setPlannerOutput("");
    setPatchOutput("");
    try {
      const data = await fetchJson(
        `/api/dev-ide/planner/runs/${selectedRun.id}/run-llm`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // NEW: include project_id for correct root resolution
          body: JSON.stringify({
            top_n: 50,
            files: chosen,
            project_id: selectedProjectId,
            max_tokens: 6000,
            model: "gpt-5",
          }),
        },
        "runLLM"
      );

      if (!data?.success) {
        const detail =
          data?.error ||
          data?.message ||
          (typeof data === "string" ? data : JSON.stringify(data));
        throw new Error(detail || "Failed to run LLM.");
      }

      const block = data.instruction_block || null;
      const text = block?.instruction || "";
      setPlannerOutput(text);

      await loadRuns();
    } catch (e) {
      setLlmError(e?.message || "Failed to run LLM.");
      setPlannerOutput("");
    } finally {
      setLlmRunning(false);
    }
  };

  // NEW: Build Patch Instructions (calls backend endpoint and displays patcher_instruction)
  const buildPatchInstructions = async () => {
    if (!selectedRun || !selectedRun.id) return;

    // GUARD-001
    const s = (statusDraft || "DRAFT").toUpperCase();
    if (s === "APPROVED") {
      setPatchError(
        "Run is APPROVED. Set status back to DRAFT/REVIEWED before generating patch instructions."
      );
      return;
    }

    // NEW: require a project so backend can scope planner_function_map by project_id
    if (!selectedProjectId) {
      setPatchError("Please select a project first.");
      return;
    }

    setPatchRunning(true);
    setPatchError("");
    setAgentError("");
    setLlmError("");
    setPatchOutput("");
    try {
      const data = await fetchJson(
        `/api/dev-ide/planner/runs/${selectedRun.id}/build-patch-instructions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // NEW: include project_id (required for function_map descriptions + path resolution)
          body: JSON.stringify({
            top_n: 50,
            max_tokens: 4000,
            project_id: selectedProjectId,
            selected_files: triageApprovedPaths,
          }),
        },
        "buildPatchInstructions"
      );

      if (!data?.success) {
        throw new Error(data?.error || "Failed to build patch instructions.");
      }

      const block = data.instruction_block || null;
      const text = block?.patcher_instruction || block?.instruction || "";
      setPatchOutput(text);

      await loadRuns();
    } catch (e) {
      setPatchError(e?.message || "Failed to build patch instructions.");
      setPatchOutput("");
    } finally {
      setPatchRunning(false);
    }
  };

  const buildPatchPreview = async () => {
    setPreviewRunning(true);
    setPreviewError("");
    setPreviewResult(null);
    setApplyError("");
    setApplyResult(null);

    if (!selectedRun?.id) {
      setPreviewError("No run selected.");
      setPreviewRunning(false);
      return;
    }
    if (!selectedProjectId) {
      setPreviewError("Please select a project first.");
      setPreviewRunning(false);
      return;
    }

    try {
      const data = await fetchJson(
        `/api/dev-ide/planner/runs/${selectedRun.id}/build-patch-preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: selectedProjectId,
            max_tokens: 15000, // <-- MINIMAL DIFF FIX (was 4000)
          }),
        },
        "buildPatchPreview"
      );

      if (data?.success === false) {
        throw new Error(data?.error || "Failed to build patch preview.");
      }

      setPreviewResult(data);
      setPreviewOpen(true);
    } catch (e) {
      setPreviewError(String(e?.message || e));
    } finally {
      setPreviewRunning(false);
    }
  };

  const applyPatchPreview = async () => {
    setApplyRunning(true);
    setApplyError("");
    setApplyResult(null);

    if (!selectedRun?.id) {
      setApplyError("No run selected.");
      setApplyRunning(false);
      return;
    }
    if (!selectedProjectId) {
      setApplyError("Please select a project first.");
      setApplyRunning(false);
      return;
    }

    try {
      const data = await fetchJson(
        `/api/dev-ide/planner/runs/${selectedRun.id}/apply-patch-preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: selectedProjectId,
            max_tokens: 15000, // <-- MINIMAL DIFF FIX (was 4000)
          }),
        },
        "applyPatchPreview"
      );

      if (data?.success === false) {
        throw new Error(data?.error || "Failed to apply patch preview.");
      }

      setApplyResult(data);
      // Refresh run list so the user sees any updated status/timestamps.
      loadRuns();
    } catch (e) {
      setApplyError(String(e?.message || e));
    } finally {
      setApplyRunning(false);
    }
  };

  // NEW: Open this planner session in the Agent App workflow (interactive UI)
  const openInAgent = () => {
    if (!selectedRun) return;

    const plannerSessionId =
      selectedPlannerSessionId ??
      selectedRun.planner_session_id ??
      selectedRun.plannerSessionId ??
      selectedRun.session_id ??
      selectedRun.id ??
      null;

    if (plannerSessionId === null || plannerSessionId === undefined) {
      setLlmError("Selected run has no planner_session_id.");
      return;
    }

    // IMPORTANT: Agent App does NOT expose /agent/planner in your current build.
    // Use /agent and pass query params so Agent UI can auto-open the goals modal.
    const url = `/agent?planner_session_id=${encodeURIComponent(
      String(plannerSessionId)
    )}&open_goals=1&source=dev_ide`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    loadProjects();
    loadRuns();
  }, []);

  useEffect(() => {
    loadGoals();
  }, [selectedRun]);

  useEffect(() => {
    setStatusError("");
    setAgentError("");
    setLlmError("");
    setPatchError("");
    setPlannerOutput("");
    setPatchOutput("");
    if (selectedRun) syncStatusDraftFromSelected(selectedRun);
  }, [selectedRun]);

  const guardMessage = useMemo(() => {
    const s = (statusDraft || "DRAFT").toUpperCase();
    if (s === "APPROVED")
      return {
        severity: "success",
        text: "Approved: planner output can be treated as approved intent.",
      };
    if (s === "REVIEWED")
      return {
        severity: "info",
        text: "Reviewed: planner output has been reviewed but not approved.",
      };
    return {
      severity: "warning",
      text: "Draft: planner output is advisory only and not yet reviewed/approved.",
    };
  }, [statusDraft]);

  return (
    <Box sx={{ display: "flex", gap: 2, height: "100%", minHeight: 0 }}>
      {/* Left: Runs */}
      <Paper
        sx={{
          width: "35%",
          p: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Planner Runs
        </Typography>

        {runsLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {runsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {runsError}
          </Alert>
        )}

        {!runsLoading && !runsError && runs.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No planner runs found.
          </Typography>
        )}

        <Box sx={{ flex: 1, overflow: "auto" }}>
          <List dense>
            {runs.map((r) => {
              const isSelected = Boolean(selectedRun && selectedRun.id === r.id);
              const primary = r.module ? `${r.module}` : `Run #${r.id}`;
              const secondaryParts = [];

              if (r.created_at) secondaryParts.push(`Created: ${r.created_at}`);
              if (r.status) secondaryParts.push(`Status: ${r.status}`);

              if (r.full_change_id) {
                const fcLabel = r.full_change_title
                  ? `Full Change: #${r.full_change_id} ${r.full_change_title}`
                  : `Full Change: #${r.full_change_id}`;
                secondaryParts.push(fcLabel);
                if (r.full_change_status)
                  secondaryParts.push(`FC Status: ${r.full_change_status}`);
              }

              if (r.goal_text)
                secondaryParts.push(String(r.goal_text).slice(0, 120));
              if (
                r.planner_session_id !== undefined &&
                r.planner_session_id !== null
              ) {
                secondaryParts.push(`Session: ${r.planner_session_id}`);
              }

              const key = r.id ?? `${primary}-${r.created_at ?? ""}`;

              return (
                <ListItemButton
                  key={key}
                  selected={isSelected}
                  onClick={() => setSelectedRun(r)}
                >
                  <ListItemText
                    primary={primary}
                    secondary={secondaryParts.filter(Boolean).join(" • ")}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Paper>

      {/* Right: Goals + Guard + Output */}
      <Paper
        sx={{
          flex: 1,
          p: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Planner Goals
        </Typography>

        {!selectedRun && (
          <Typography variant="body2" color="text.secondary">
            Select a planner run to view goals.
          </Typography>
        )}

        {selectedRun && (
          <>
            <Divider sx={{ mb: 2 }} />

            <Alert severity={guardMessage.severity} sx={{ mb: 2 }}>
              {guardMessage.text}
            </Alert>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 2, alignItems: "center", flexWrap: "wrap" }}
            >
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Project</InputLabel>
                <Select
                  label="Project"
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  disabled={
                    projectsLoading ||
                    statusSaving ||
                    agentRunning ||
                    llmRunning ||
                    patchRunning ||
                    previewRunning ||
                    applyRunning
                  }
                >
                  {projects.map((p) => (
                    <MenuItem key={p.project_id} value={p.project_id}>
                      {p.label ? `${p.label} (${p.project_id})` : p.project_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusDraft}
                  onChange={(e) => setStatusDraft(e.target.value)}
                  disabled={
                    statusSaving || agentRunning || llmRunning || patchRunning
                  }
                >
                  <MenuItem value="DRAFT">DRAFT</MenuItem>
                  <MenuItem value="REVIEWED">REVIEWED</MenuItem>
                  <MenuItem value="APPROVED">APPROVED</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={saveStatus}
                disabled={statusSaving || agentRunning || llmRunning || patchRunning}
              >
                {statusSaving ? "Saving..." : "Save Status"}
              </Button>

              <Button
                variant="outlined"
                onClick={runAgent}
                disabled={agentRunning || llmRunning || patchRunning}
              >
                {agentRunning ? "Running..." : "Run Agent"}
              </Button>

              <Button
                variant="outlined"
                onClick={runLLM}
                disabled={llmRunning || agentRunning || patchRunning}
              >
                {llmRunning ? "Running..." : "Run LLM"}
              </Button>

              <Button
                variant="outlined"
                onClick={buildPatchInstructions}
                disabled={patchRunning || agentRunning || llmRunning}
              >
                {patchRunning ? "Building..." : "Build Patch Instructions"}
              </Button>

              <Button
                variant="outlined"
                onClick={buildPatchPreview}
                disabled={
                  previewRunning ||
                  patchRunning ||
                  agentRunning ||
                  llmRunning ||
                  !selectedProjectId
                }
              >
                {previewRunning ? "Building Preview..." : "Build Patch Preview"}
              </Button>

              <Button
                variant="outlined"
                color="warning"
                onClick={applyPatchPreview}
                disabled={
                  applyRunning ||
                  previewRunning ||
                  patchRunning ||
                  agentRunning ||
                  llmRunning ||
                  !selectedProjectId
                }
              >
                {applyRunning ? "Applying..." : "Apply Patch Preview"}
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                onClick={openInAgent}
                disabled={agentRunning || llmRunning || patchRunning}
              >
                Open in Agent
              </Button>

              {statusError && (
                <Typography variant="body2" color="error">
                  {statusError}
                </Typography>
              )}

              {agentError && (
                <Typography variant="body2" color="error">
                  {agentError}
                </Typography>
              )}

              {llmError && (
                <Typography variant="body2" color="error">
                  {llmError}
                </Typography>
              )}

              {patchError && (
                <Typography variant="body2" color="error">
                  {patchError}
                </Typography>
              )}

              {projectsError && (
                <Typography variant="body2" color="error">
                  {projectsError}
                </Typography>
              )}

              {previewError && (
                <Typography variant="body2" color="error">
                  {previewError}
                </Typography>
              )}

              {applyError && (
                <Typography variant="body2" color="error">
                  {applyError}
                </Typography>
              )}
            </Stack>

            {goalsLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                <CircularProgress size={22} />
              </Box>
            )}

            {goalsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {goalsError}
              </Alert>
            )}

            {!goalsLoading && !goalsError && goals.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No goals found for this run/session.
              </Typography>
            )}

            <Box sx={{ flex: 1, overflow: "auto" }}>
              <List>
                {goals.map((g) => {
                  const title =
                    g.goal_text || g.description || g.category || `Goal #${g.id}`;
                  const metaParts = [];
                  if (g.category) metaParts.push(`Category: ${g.category}`);
                  if (g.active !== undefined && g.active !== null)
                    metaParts.push(`Active: ${g.active ? "Yes" : "No"}`);
                  if (g.created_at) metaParts.push(`Created: ${g.created_at}`);

                  return (
                    <React.Fragment key={g.id ?? title}>
                      <ListItemText
                        primary={title}
                        secondary={metaParts.filter(Boolean).join(" • ")}
                        sx={{ py: 1 }}
                      />
                      <Divider />
                    </React.Fragment>
                  );
                })}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Planner Output (Read-only)
              </Typography>

              {!plannerOutput && (
                <Typography variant="body2" color="text.secondary">
                  Click “Run Agent” to generate a draft plan, then “Run LLM” to
                  generate a richer plan.
                </Typography>
              )}

              {plannerOutput && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: 13,
                    overflow: "auto",
                    maxHeight: 320,
                  }}
                >
                  {plannerOutput}
                </Paper>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Patch Instructions (Read-only)
              </Typography>

              {!patchOutput && (
                <Typography variant="body2" color="text.secondary">
                  Click “Build Patch Instructions” to generate per-file patch
                  steps.
                </Typography>
              )}

              {patchOutput && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: 13,
                    overflow: "auto",
                    maxHeight: 320,
                  }}
                >
                  {patchOutput}
                </Paper>
              )}
            </Box>
          </>
        )}
      </Paper>

      {/* NEW: File Triage Dialog */}
      <PlannerFileTriageDialog
        open={triageOpen}
        onClose={() => {
          if (!llmRunning) setTriageOpen(false);
        }}
        module={(selectedRun?.module || "devtools").toString()}
        goalText={(selectedRun?.goal_text || selectedRun?.goalText || "").toString()}
        topN={30}
        projectId={selectedProjectId || null} // <-- MINIMAL DIFF FIX
        onConfirm={(paths) => {
          const arr = Array.isArray(paths) ? paths.filter(Boolean) : [];
          setTriageOpen(false);
          runLLMWithFiles(arr);
        }}
      />


      {/* NEW: Patch Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => {
          if (!previewRunning && !applyRunning) setPreviewOpen(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Patch Preview</DialogTitle>
        <DialogContent dividers>
          {previewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {previewError}
            </Alert>
          )}

          {applyError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {applyError}
            </Alert>
          )}

          {applyResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Applied {applyResult?.applied?.length || 0} file(s).
            </Alert>
          )}

          {!previewResult && !previewRunning && (
            <Typography variant="body2" color="text.secondary">
              No preview result yet. Click “Build Patch Preview”.
            </Typography>
          )}

          {previewRunning && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Building preview…</Typography>
            </Stack>
          )}

          {previewResult && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                instruction_block_id: {previewResult.instruction_block_id} • targets:{" "}
                {previewResult.targets?.length || 0}
              </Typography>

              {(previewResult.previews || []).map((p, idx) => (
                <Box key={idx} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {p.path} {p.success ? "" : "(error)"}
                  </Typography>

                  {!p.success && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      {p.error || "Failed to build preview."}
                    </Alert>
                  )}

                  {p.success && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        whiteSpace: "pre",
                        fontFamily: "monospace",
                        fontSize: 12,
                        overflow: "auto",
                        maxHeight: 320,
                      }}
                    >
                      {p.diff || "(no diff)"}
                    </Paper>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPreviewOpen(false)}
            disabled={previewRunning || applyRunning}
          >
            Close
          </Button>
          <Button
            onClick={buildPatchPreview}
            variant="outlined"
            disabled={previewRunning || applyRunning}
          >
            Build Preview
          </Button>
          <Button
            onClick={applyPatchPreview}
            variant="contained"
            disabled={previewRunning || applyRunning || !previewResult}
          >
            {applyRunning ? "Applying…" : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlannerPanel;

// Read-only Planner API helpers

export async function fetchPlannerRuns() {
  const res = await fetch("/api/dev-ide/planner/runs");
  if (!res.ok) {
    throw new Error("Failed to load planner runs");
  }
  return res.json();
}

export async function fetchPlannerGoals(plannerSessionId) {
  if (!plannerSessionId) return [];

  const res = await fetch(
    `/api/dev-ide/planner/goals?planner_session_id=${plannerSessionId}`
  );
  if (!res.ok) {
    throw new Error("Failed to load planner goals");
  }
  return res.json();
}